import type { Point } from './world-types.js';

/**
 * Centripetal Catmull-Rom through map path points — the same curve the renderer draws with Three's
 * `CatmullRomCurve3(..., 'centripetal')`, so content can place things relative to the true road surface.
 */
export function samplePath(points: Point[], samples: number): Point[] {
  if (points.length < 2) return [...points];
  const at = (i: number): Point => i < 0 ? { x: 2 * points[0].x - points[1].x, z: 2 * points[0].z - points[1].z }
    : i >= points.length ? { x: 2 * points.at(-1)!.x - points.at(-2)!.x, z: 2 * points.at(-1)!.z - points.at(-2)!.z } : points[i];
  const out: Point[] = [];
  for (let s = 0; s <= samples; s++) {
    const t = s / samples, p = (points.length - 1) * t;
    let i = Math.floor(p), w = p - i;
    if (w === 0 && i === points.length - 1) { i = points.length - 2; w = 1; }
    const [p0, p1, p2, p3] = [at(i - 1), at(i), at(i + 1), at(i + 2)];
    const d = (a: Point, b: Point) => Math.max(Math.pow((a.x - b.x) ** 2 + (a.z - b.z) ** 2, .25), 1e-4);
    const [dt0, dt1, dt2] = [d(p0, p1), d(p1, p2), d(p2, p3)];
    const axis = (key: 'x' | 'z') => {
      const [x0, x1, x2, x3] = [p0[key], p1[key], p2[key], p3[key]];
      const t1 = ((x1 - x0) / dt0 - (x2 - x0) / (dt0 + dt1) + (x2 - x1) / dt1) * dt1;
      const t2 = ((x2 - x1) / dt1 - (x3 - x1) / (dt1 + dt2) + (x3 - x2) / dt2) * dt1;
      return x1 + t1 * w + (-3 * x1 + 3 * x2 - 2 * t1 - t2) * w * w + (2 * x1 - 2 * x2 + t1 + t2) * w * w * w;
    };
    out.push({ x: axis('x'), z: axis('z') });
  }
  return out;
}
